/**
 * Countdown Timer Module.
 * Handles countdown functionality with start/end time validation and automatic hiding.
 */
class CountdownTimer {
  /**
   * Initialise the countdown timer.
   * @param {Object} options - Configuration options for the timer.
   * @param {string} options.endTime - End time in ISO string format.
   * @param {string} options.startTime - Start time in ISO string format.
   * @param {HTMLElement} options.container - The timer container element.
   */
  constructor(options) {
    this.endTime = options.endTime;
    this.startTime = options.startTime;
    this.container = options.container;
    this.intervalId = null;
    this.isVisible = false;

    // Cache DOM elements for better performance.
    this.daysElement = this.container?.querySelector('.js-timer-days');
    this.hoursElement = this.container?.querySelector('.js-timer-hours');
    this.minutesElement = this.container?.querySelector('.js-timer-minutes');
    this.secondsElement = this.container?.querySelector('.js-timer-seconds');

    // Cache label elements for singular/plural switching.
    this.daysLabelElement = this.container?.querySelector('.js-timer-days-label');
    this.hoursLabelElement = this.container?.querySelector('.js-timer-hours-label');
    this.minutesLabelElement = this.container?.querySelector('.js-timer-minutes-label');
    this.secondsLabelElement = this.container?.querySelector('.js-timer-seconds-label');

    this.init();
  }

  /**
   * Initialise the countdown timer.
   */
  init() {
    // Early return if required elements are missing.
    if (!this.container || !this.endTime || !this.startTime) {
      console.warn('Countdown timer: Missing required elements or time values.');
      return;
    }

    // Parse the time strings.
    const startDate = new Date(this.startTime);
    const endDate = new Date(this.endTime);
    const now = new Date();

    // Validate date parsing.
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Countdown timer: Invalid date format provided.');
      return;
    }

    // Check if we should show the timer.
    if (now < startDate) {
      this.hideTimer();
      return;
    }

    // Check if the timer has already ended.
    if (now >= endDate) {
      this.hideTimer();
      return;
    }

    // Show and start the timer.
    this.showTimer();
    this.startCountdown(endDate);
  }

  /**
   * Start the countdown countdown.
   * @param {Date} endDate - The end date for the countdown.
   */
  startCountdown(endDate) {
    // Clear any existing interval.
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Update immediately and then every second.
    this.updateTimer(endDate);
    this.intervalId = setInterval(() => {
      this.updateTimer(endDate);
    }, 1000);
  }

  /**
   * Update the timer display.
   * @param {Date} endDate - The end date for the countdown.
   */
  updateTimer(endDate) {
    const now = new Date();
    const timeDifference = endDate.getTime() - now.getTime();

    // Check if countdown has ended.
    if (timeDifference <= 0) {
      this.hideTimer();
      return;
    }

    // Calculate time units.
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    // Update DOM elements if they exist.
    this.updateElement(this.daysElement, days);
    this.updateElement(this.hoursElement, hours);
    this.updateElement(this.minutesElement, minutes);
    this.updateElement(this.secondsElement, seconds);

    // Update labels with singular/plural logic.
    this.updateLabels(days, hours, minutes, seconds);
  }

  /**
   * Update a specific timer element.
   * @param {HTMLElement|null} element - The element to update.
   * @param {number} value - The value to display.
   */
  updateElement(element, value) {
    if (element) {
      // Show single digits with leading zeros (01, 02, 03 for consistent formatting).
      element.textContent = value.toString().padStart(2, '0');
    }
  }

  /**
   * Update timer labels with singular/plural logic.
   * @param {number} days - Number of days.
   * @param {number} hours - Number of hours.
   * @param {number} minutes - Number of minutes.
   * @param {number} seconds - Number of seconds.
   */
  updateLabels(days, hours, minutes, seconds) {
    // Update days label.
    if (this.daysLabelElement) {
      const daysText =
        days === 1 && this.daysLabelElement.dataset.singular
          ? this.daysLabelElement.dataset.singular
          : this.daysLabelElement.dataset.plural;
      this.daysLabelElement.textContent = daysText;
    }

    // Update hours label.
    if (this.hoursLabelElement) {
      const hoursText =
        hours === 1 && this.hoursLabelElement.dataset.singular
          ? this.hoursLabelElement.dataset.singular
          : this.hoursLabelElement.dataset.plural;
      this.hoursLabelElement.textContent = hoursText;
    }

    // Update minutes label.
    if (this.minutesLabelElement) {
      const minutesText =
        minutes === 1 && this.minutesLabelElement.dataset.singular
          ? this.minutesLabelElement.dataset.singular
          : this.minutesLabelElement.dataset.plural;
      this.minutesLabelElement.textContent = minutesText;
    }

    // Update seconds label.
    if (this.secondsLabelElement) {
      const secondsText =
        seconds === 1 && this.secondsLabelElement.dataset.singular
          ? this.secondsLabelElement.dataset.singular
          : this.secondsLabelElement.dataset.plural;
      this.secondsLabelElement.textContent = secondsText;
    }
  }

  /**
   * Show the countdown timer.
   */
  showTimer() {
    if (this.container && !this.isVisible) {
      this.container.style.display = 'block';
      this.isVisible = true;
    }
  }

  /**
   * Hide the countdown timer.
   */
  hideTimer() {
    if (this.container && this.isVisible) {
      this.container.style.display = 'none';
      this.isVisible = false;
    }

    // Clean up the interval.
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Clean up event listeners and intervals.
   */
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

/**
 * Initialise all countdown timers on the page.
 */
function initCountdownTimers() {
  const timerContainers = document.querySelectorAll('.countdown-timer__display');

  timerContainers.forEach((container) => {
    const endTime = container.dataset.countdownTime;

    // Early return if no end time is provided.
    if (!endTime) {
      console.warn('Countdown timer: Missing countdown time data attribute.');
      return;
    }

    // Find the parent countdown timer section to get start time.
    const countdownSection = container.closest('.countdown-timer');
    if (!countdownSection) return;

    // Extract start time from the section's data or use a default.
    const startTime = countdownSection.dataset.startTime || new Date().toISOString();

    // Create and initialise the timer.
    new CountdownTimer({
      endTime,
      startTime,
      container: countdownSection
    });
  });
}

// Initialise timers when DOM is ready.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCountdownTimers);
} else {
  initCountdownTimers();
}

// Export for potential external use.
export { CountdownTimer, initCountdownTimers };
